import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import ChangePasswordDialog from './ChangePasswordDialog';
import PushInboxBell from './PushInboxBell';
import { rolePermissionsApi, accessDenialsApi } from '../lib/api';
import { 
  House, 
  GraduationCap, 
  ChalkboardTeacher, 
  Users,
  Money, 
  ChartBar, 
  Certificate, 
  Receipt,
  Package,
  CalendarCheck,
  Gear,
  Wrench,
  SignOut,
  List,
  X,
  Archive,
  Trash as TrashIcon,
  Globe,
  EnvelopeSimple,
  ForkKnife,
  Bus,
  Lightning,
  Key,
  ShieldCheck,
  Bell,
  ArrowUUpLeft,
  PaperPlaneTilt,
  Fingerprint,
  Lifebuoy,
  UserMinus,
  Calendar
} from '@phosphor-icons/react';

const Layout = ({ children }) => {
  const { user, logout, hasRole } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [forcedExpired, setForcedExpired] = useState(false);
  const [pageOverride, setPageOverride] = useState(null);
  const [denialCount, setDenialCount] = useState(0);

  // Admin only: poll the access-denial count so a red badge warns about
  // profiles hitting blocked pages (missing grants).
  useEffect(() => {
    if (!user || user.role !== 'admin') { setDenialCount(0); return; }
    let cancelled = false;
    const fetchCount = () => accessDenialsApi.count()
      .then(({ data }) => { if (!cancelled) setDenialCount(data?.count || 0); })
      .catch(() => {});
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, location.pathname]);

  // Load admin-managed page overrides for this profile (null = use code defaults).
  useEffect(() => {
    let cancelled = false;
    if (!user || user.role === 'admin') { setPageOverride(null); return; }
    rolePermissionsApi.getMine()
      .then(({ data }) => { if (!cancelled) setPageOverride(Array.isArray(data?.allowed_paths) ? data.allowed_paths : null); })
      .catch(() => { if (!cancelled) setPageOverride(null); });
    return () => { cancelled = true; };
  }, [user]);

  // If the backend flagged the password as expired (admin/comptable, >90 days),
  // force the change-password dialog open immediately and refuse to dismiss it.
  useEffect(() => {
    if (user?.password_expired && !forcedExpired) {
      setPwDialogOpen(true);
      setForcedExpired(true);
    }
  }, [user, forcedExpired]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // iter102 — Every staff account (web or PIN) can justify its own absences.
  const STAFF_ROLES = ['admin', 'rh', 'director_studies', 'comptable', 'cashier', 'secretary', 'user', 'staff_user', 'teacher_user', 'vigil'];

  const navItems = [
    { path: '/', icon: House, label: t('dashboard'), roles: ['admin', 'comptable', 'cashier', 'director_general'] },
    { path: '/director-kpi', icon: ChartBar, label: language === 'fr' ? 'Tableau Directeur' : 'Director KPI', roles: ['admin', 'director_general'] },
    { path: '/students', icon: GraduationCap, label: t('students'), roles: ['admin', 'user', 'director_studies'] },
    { path: '/staff', icon: ChalkboardTeacher, label: t('staff'), roles: ['admin', 'user', 'rh'] },
    { path: '/payments', icon: Money, label: t('payments'), roles: ['admin', 'cashier'] },
    { path: '/grades', icon: Certificate, label: 'Notes & Bulletins', roles: ['admin', 'director_studies', 'user'] },
    { path: '/director', icon: ChartBar, label: t('director'), roles: ['admin', 'director_studies', 'director_general'] },
    { path: '/year-end', icon: GraduationCap, label: 'Fin d\'année', roles: ['admin', 'director_studies'] },
    { path: '/archived-students', icon: Archive, label: 'Archives élèves', roles: ['admin', 'director_studies'] },
    { path: '/trash', icon: TrashIcon, label: 'Corbeille', roles: ['admin'] },
    { path: '/hr', icon: Users, label: 'RH', roles: ['admin', 'rh', 'director_general'] },
    { path: '/secretariat', icon: EnvelopeSimple, label: 'Secrétariat', roles: ['admin', 'user', 'secretary', 'director_general'] },
    { path: '/reports', icon: ChartBar, label: t('reports'), roles: ['admin', 'comptable', 'director_general'] },
    { path: '/expenses', icon: Receipt, label: t('expenses'), roles: ['admin', 'comptable', 'director_general'] },
    { path: '/supplies', icon: Package, label: t('supplies'), roles: ['admin', 'user'] },
    { path: '/attendance', icon: CalendarCheck, label: `${t('attendance')} & ${t('salaries')}`, roles: ['admin', 'rh'] },
    { path: '/biometric-punch', icon: Fingerprint, label: language === 'fr' ? 'Pointage empreinte' : 'Fingerprint punch', roles: ['admin', 'rh', 'director_general'] },
    { path: '/compliance-overview', icon: ChartBar, label: language === 'fr' ? 'Conformité paie' : 'Payroll compliance', roles: ['admin', 'rh', 'comptable', 'director_general'] },
    { path: '/absences', icon: CalendarCheck, label: language === 'fr' ? 'Justification absences' : 'Absence justifications', roles: ['admin', 'director_studies', 'user', 'staff_user', 'director_general'], hideForStaffTypes: ['cleaning'] },
    { path: '/attendance-stats', icon: ChartBar, label: language === 'fr' ? 'Stats présence' : 'Attendance stats', roles: ['admin', 'director_studies', 'user', 'staff_user', 'director_general'], hideForStaffTypes: ['cleaning'] },
    { path: '/support-plans', icon: Lifebuoy, label: language === 'fr' ? 'Plans de soutien' : 'Support plans', roles: ['admin', 'director_studies', 'director_general'] },
    { path: '/staff-absences', icon: UserMinus, label: language === 'fr' ? 'Absences personnel' : 'Staff absences', roles: ['admin', 'rh', 'director_general'] },
    { path: '/my-absences', icon: CalendarCheck, label: language === 'fr' ? 'Mes absences' : 'My absences', roles: STAFF_ROLES },
    { path: '/security', icon: ShieldCheck, label: language === 'fr' ? 'Sécurité' : 'Security', roles: ['admin', 'cashier', 'staff_user', 'vigil'], hideForStaffTypes: ['cleaning'] },
    { path: '/daily-lists', icon: Calendar, label: language === 'fr' ? 'Listes journalières' : 'Daily lists', roles: ['admin', 'comptable', 'cashier', 'director_general'] },
    { path: '/appointments', icon: CalendarCheck, label: t('appointments'), roles: ['admin', 'director_studies', 'rh', 'director_general'] },
    { path: '/timetables', icon: CalendarCheck, label: t('timetables'), roles: ['admin', 'director_studies', 'director_general'] },
    { path: '/cantine-menus', icon: ForkKnife, label: t('cantineMenus'), roles: ['admin', 'user', 'director_general'] },
    { path: '/transport-routes', icon: Bus, label: t('transportRoutes'), roles: ['admin', 'user', 'director_general'] },
    { path: '/messages', icon: EnvelopeSimple, label: t('messages'), roles: ['admin', 'director_studies', 'user'] },
    { path: '/notifications', icon: EnvelopeSimple, label: t('notifications'), roles: ['admin'] },
    { path: '/users', icon: Users, label: t('users'), roles: ['admin'] },
    { path: '/role-permissions', icon: ShieldCheck, label: language === 'fr' ? 'Droits par profil' : 'Role permissions', roles: ['admin'] },
    { path: '/settings', icon: Gear, label: t('schoolSettings'), roles: ['admin'] },
    { path: '/tools', icon: Wrench, label: language === 'fr' ? 'Outils' : 'Tools', roles: ['admin'] },
    { path: '/admin/performance', icon: Lightning, label: language === 'fr' ? 'Performance' : 'Performance', roles: ['admin'] },
    { path: '/admin/security', icon: ShieldCheck, label: language === 'fr' ? 'Sécurité' : 'Security', roles: ['admin'] },
    { path: '/admin/notifications', icon: Bell, label: language === 'fr' ? 'Notifications' : 'Notifications', roles: ['admin'] },
    { path: '/admin/push-broadcast', icon: PaperPlaneTilt, label: language === 'fr' ? 'Broadcast Push' : 'Push Broadcast', roles: ['admin'] },
    { path: '/admin/bulk-operations', icon: ArrowUUpLeft, label: language === 'fr' ? 'Historique masse (Undo)' : 'Bulk history (Undo)', roles: ['admin'] },
  ];

  const filteredNavItems = navItems.filter(item => {
    const visibleByRole = pageOverride ? pageOverride.includes(item.path) : hasRole(item.roles);
    return visibleByRole
      && !(item.hideForStaffTypes && user?.role !== 'admin'
        && item.hideForStaffTypes.includes(user?.staff_type));
  });

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-[#E2E8F0] z-50 flex items-center justify-between px-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="hover:bg-[#F1F5F9]"
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X size={24} /> : <List size={24} />}
        </Button>
        <img 
          src="https://static.prod-images.emergentagent.com/jobs/568a569c-0fa6-40dc-9d3f-d310e1c010bc/images/8563b961e801bb242cecdbdafd9d4cf288a10e83064dc5a2edd376c57200f0da.png"
          alt="Logo"
          className="h-8 w-8"
        />
        <div className="flex items-center gap-1">
          <PushInboxBell compact />
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleLanguage}
            className="hover:bg-[#F1F5F9]"
            data-testid="mobile-language-toggle"
          >
            <Globe size={20} />
          </Button>
        </div>
      </div>

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-[#E2E8F0] z-40 transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="h-16 flex items-center justify-center border-b border-[#E2E8F0] px-4">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/568a569c-0fa6-40dc-9d3f-d310e1c010bc/images/8563b961e801bb242cecdbdafd9d4cf288a10e83064dc5a2edd376c57200f0da.png"
              alt="Logo"
              className="h-10 w-10 mr-3"
            />
            <span className="font-outfit font-bold text-[#0F172A] text-lg">
              {language === 'fr' ? 'Gestion École' : 'School Mgmt'}
            </span>
          </div>

          {/* User Info */}
          <div className="px-4 py-4 border-b border-[#E2E8F0]">
            <p className="font-manrope font-medium text-[#0F172A] truncate">{user?.name}</p>
            <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#EFF6FF] text-[#0052FF]">
              {user?.is_custom_role
                ? (language === 'fr' ? (user?.role_label_fr || user?.role) : (user?.role_label_en || user?.role_label_fr || user?.role))
                : t(user?.role)}
            </span>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="space-y-1 px-2">
              {filteredNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg font-manrope text-sm transition-all duration-200
                      ${isActive 
                        ? 'bg-[#0052FF] text-white' 
                        : 'text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A]'}`}
                    data-testid={`nav-${item.path.slice(1) || 'home'}`}
                  >
                    <item.icon size={20} weight={isActive ? 'fill' : 'regular'} />
                    <span className="flex-1">{item.label}</span>
                    {item.path === '/role-permissions' && denialCount > 0 && (
                      <span
                        className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold
                          ${isActive ? 'bg-white text-[#0052FF]' : 'bg-[#EF4444] text-white'}`}
                        data-testid="denial-badge"
                        title={language === 'fr' ? 'Accès refusés à traiter' : 'Access denials to review'}
                      >
                        {denialCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* Footer */}
          <div className="p-4 border-t border-[#E2E8F0] space-y-2">
            <Button
              variant="ghost"
              onClick={() => setPwDialogOpen(true)}
              className="w-full justify-start text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9]"
              data-testid="change-password-btn"
            >
              <Key size={20} className="mr-3" />
              {language === 'fr' ? 'Mot de passe' : 'Password'}
            </Button>
            <Button
              variant="ghost"
              onClick={toggleLanguage}
              className="w-full justify-start text-[#64748B] hover:text-[#0F172A] hover:bg-[#F1F5F9]"
              data-testid="sidebar-language-toggle"
            >
              <Globe size={20} className="mr-3" />
              {language === 'fr' ? 'English' : 'Français'}
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-start text-[#EF4444] hover:text-[#EF4444] hover:bg-[#FEF2F2]"
              data-testid="logout-btn"
            >
              <SignOut size={20} className="mr-3" />
              {t('logout')}
            </Button>
          </div>
        </div>
      </aside>

      <ChangePasswordDialog
        open={pwDialogOpen}
        onOpenChange={(v) => {
          // If password is expired, the user must change it — block all close attempts.
          if (user?.password_expired && v === false) return;
          setPwDialogOpen(v);
        }}
        forced={!!user?.password_expired}
      />

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        {/* Desktop-only floating bell (top-right) */}
        <div className="hidden lg:flex fixed top-4 right-6 z-30 items-center gap-1 bg-white border border-[#E2E8F0] rounded-full shadow-sm px-1 py-1">
          <PushInboxBell />
        </div>
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
